/**
 * LOGOI LAB - Layer 3: Watson Energy Landscape
 * Computes domain scores and optimal trajectory
 * 
 * @author Dr. José Manuel Cadena Ortiz de Montellano
 * @framework Cadena Strategic Systems
 */

import type { ConsciousnessState, WatsonResult, DomainKey } from '../types';
import { DOMAINS } from '../domain/constants';

/**
 * Compute Watson Energy Landscape
 * 
 * Calculates energy distance from optimal attractor for each domain.
 * The optimal attractor IS Logos-modulated.
 * 
 * @param s - Current consciousness state
 * @param Lambda - Current Logos alignment value
 * @returns WatsonResult with energy, domain scores, and optimal targets
 */
export function computeWatsonEnergy(s: ConsciousnessState, Lambda: number): WatsonResult {
  const domainScores: Record<string, number> = {};
  const state = s as any;
  
  // Calculate average for each domain
  for (const [dk, dv] of Object.entries(DOMAINS)) {
    const keys = Object.keys(dv.dims);
    domainScores[dk] = keys.reduce((sum, k) => sum + (state[k] ?? 0.5), 0) / keys.length;
  }
  
  // Optimal attractor IS Logos-modulated
  const optimal: Record<string, number> = {
    physical: 0.70 + Lambda * 0.15,
    emotional: 0.65 + Lambda * 0.15,
    mental: 0.65 + Lambda * 0.15,
    spiritual: 0.60 + Lambda * 0.20,
    relational: 0.60 + Lambda * 0.15,
    purpose: 0.60 + Lambda * 0.20,
    alimento: 0.65 + Lambda * 0.15,
  };
  
  let E = 0;
  const breakdown: any[] = [];
  
  for (const dk of Object.keys(optimal)) {
    const diff = domainScores[dk] - optimal[dk];
    const contrib = diff * diff;
    E += contrib;
    breakdown.push({ 
      domain: dk, 
      label: DOMAINS[dk as DomainKey].label, 
      actual: domainScores[dk], 
      optimal: optimal[dk], 
      gap: diff, 
      contribution: contrib 
    });
  }
  
  return { 
    energy: Math.sqrt(E), 
    domainScores: domainScores as Record<DomainKey, number>, 
    optimal: optimal as Record<DomainKey, number>, 
    breakdown 
  };
}
